/**
 * Snowflake Connector – JDBC/key-pair auth (no OAuth).
 * Uses connector parameters: account_id, warehouse, database, authenticator, username, privatekey [, role, schema].
 * Base URL is built from account_id only: jdbc:snowflake://{account_id}.snowflakecomputing.com
 */

import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

const token = localStorage.getItem('token');

/**
 * Get Snowflake credentials from connector parameters, test connection, then mark connector connected.
 * Parameters (from sync-connector-parameters/connector/{connectorId}):
 *   account_id, warehouse, database, authenticator, username, privatekey [, role, schema]
 */
export const getSnowflakeCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    const snowflakeConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() || mc.mcId?.toString() === mcId?.toString()) &&
      ((mc.mcName && mc.mcName.toLowerCase().includes('snowflake')) ||
        (mc.mcConnectorType && mc.mcConnectorType.toUpperCase().includes('SNOWFLAKE')))
    );

    if (!snowflakeConnector) {
      throw new Error('Snowflake master connector not found');
    }

    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!paramResponse.ok) {
      throw new Error(`Failed to fetch connector parameters: ${paramResponse.status}`);
    }

    const connectorParams = await paramResponse.json();

    const accountId = connectorParams.find((p) => p.scpName === 'account_id')?.scpValue;
    const warehouse = connectorParams.find((p) => p.scpName === 'warehouse')?.scpValue;
    const database = connectorParams.find((p) => p.scpName === 'database')?.scpValue;
    const authenticator = connectorParams.find((p) => p.scpName === 'authenticator')?.scpValue;
    const username = connectorParams.find((p) => p.scpName === 'username')?.scpValue;
    const privateKey = connectorParams.find((p) => p.scpName === 'privatekey')?.scpValue;
    const role = connectorParams.find((p) => p.scpName === 'role')?.scpValue;
    const schema = connectorParams.find((p) => p.scpName === 'schema')?.scpValue;

    const missing = [];
    if (!accountId) missing.push('account_id');
    if (!warehouse) missing.push('warehouse');
    if (!database) missing.push('database');
    if (!authenticator) missing.push('authenticator');
    if (!username) missing.push('username');
    if (!privateKey) missing.push('privatekey');

    if (missing.length) {
      throw new Error(
        `Missing required Snowflake parameters: ${missing.join(', ')}. Please configure them in Parameters settings.`
      );
    }

    const payload = {
      connectorId,
      account_id: accountId,
      warehouse,
      database,
      authenticator: authenticator || 'snowflake_jwt',
      username,
      privatekey: privateKey,
      ...(role && { role }),
      ...(schema && { schema }),
    };

    const testResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}synctrack/snowflake-connector/test`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!testResponse.ok) {
      const errorData = await testResponse.json().catch(() => ({ message: 'Connection test failed' }));
      throw new Error(errorData.message || 'Failed to test Snowflake connection');
    }

    const connectResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}synctrack/snowflake-connector/connect`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ connectorId }),
      }
    );

    if (!connectResponse.ok) {
      const errorData = await connectResponse.json().catch(() => ({ message: 'Connection failed' }));
      throw new Error(errorData.message || 'Failed to connect Snowflake');
    }

    return {
      connected: true,
      message: 'Snowflake connected successfully',
      accountId,
      warehouse,
      database,
    };
  } catch (error) {
    console.error('Error getting Snowflake credentials:', error);
    throw error;
  }
};
